import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Leaf,
  HandHeart,
  HardHat,
  Scale,
  Map,
  FileBarChart,
  FolderOpen,
  Bell,
  Users,
  Settings,
  Sparkles,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import logoViabiliza from "@/assets/logo-viabiliza.png";

type Item = { title: string; url: string; icon: React.ComponentType<{ className?: string }>; color?: string };

const mainItems: Item[] = [
  { title: "Dashboard", url: "/home", icon: LayoutDashboard },
];

const modules: Item[] = [
  { title: "Ambiental", url: "/ambiental", icon: Leaf, color: "text-module-ambiental" },
  { title: "Social", url: "/social", icon: HandHeart, color: "text-module-social" },
  { title: "SST", url: "/sst", icon: HardHat, color: "text-module-sst" },
  { title: "Legal", url: "/legal", icon: Scale, color: "text-module-legal" },
  { title: "GDB", url: "/gestor-gdb", icon: Map, color: "text-module-gdb" },
  { title: "Reporte ICA", url: "/reporte-integrado", icon: FileBarChart, color: "text-module-ica" },
];

const tools: Item[] = [
  { title: "Documentos", url: "/documentos-sociales", icon: FolderOpen },
  { title: "Herramientas", url: "/herramientas", icon: Sparkles },
  { title: "Notificaciones", url: "/configuracion-notificaciones", icon: Bell },
];

const admin: Item[] = [
  { title: "Usuarios", url: "/gestion-usuarios", icon: Users },
  { title: "Configuración", url: "/configuraciones", icon: Settings },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const { pathname } = useLocation();
  const collapsed = state === "collapsed";

  const isActive = (url: string) =>
    pathname === url || (url !== "/home" && pathname.startsWith(url));

  const renderGroup = (label: string, items: Item[]) => (
    <SidebarGroup>
      {!collapsed && (
        <SidebarGroupLabel className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground/70 px-3">
          {label}
        </SidebarGroupLabel>
      )}
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.url);
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  tooltip={collapsed ? item.title : undefined}
                  className={cn(
                    "h-9 rounded-lg transition-all",
                    active
                      ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium shadow-sm"
                      : "hover:bg-sidebar-accent/60"
                  )}
                >
                  <NavLink to={item.url} className="flex items-center gap-2.5">
                    <Icon
                      className={cn(
                        "h-4 w-4 shrink-0 transition-colors",
                        active ? item.color ?? "text-brand" : "text-muted-foreground"
                      )}
                    />
                    {!collapsed && <span className="text-sm">{item.title}</span>}
                    {active && !collapsed && (
                      <span className="ml-auto h-1.5 w-1.5 rounded-full bg-brand" />
                    )}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="border-b border-sidebar-border h-16 px-3 flex flex-row items-center gap-2">
        <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-gradient-brand shadow-sm shrink-0">
          <img src={logoViabiliza} alt="Viabiliza" className="h-7 w-7 object-contain" />
        </div>
        {!collapsed && (
          <div className="flex flex-col leading-none overflow-hidden">
            <span className="font-bold text-sidebar-foreground text-base tracking-tight">Viabiliza</span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Enterprise</span>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="px-2 py-3 gap-1">
        {renderGroup("General", mainItems)}
        {renderGroup("Módulos", modules)}
        {renderGroup("Herramientas", tools)}
        {renderGroup("Administración", admin)}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-3">
        {!collapsed ? (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
            Sistema operativo
          </div>
        ) : (
          <span className="h-2 w-2 rounded-full bg-success mx-auto animate-pulse" />
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
